import { useStats } from "../hooks/useStats";
import { useHealth } from "../hooks/useHealth";
import { useSources } from "../hooks/useSources";
import type { SystemMetrics } from "../types";

function GaugeRing({ percent, color, label, detail }: {
  percent: number;
  color: string;
  label: string;
  detail?: string;
}) {
  const radius = 40;
  const stroke = 7;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (percent / 100) * circumference;

  return (
    <div className="gauge-ring-container">
      <svg width={100} height={100} viewBox="0 0 100 100" className="gauge-ring-svg">
        <circle
          cx="50" cy="50" r={radius}
          fill="none"
          stroke="var(--border-light)"
          strokeWidth={stroke}
        />
        <circle
          cx="50" cy="50" r={radius}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          strokeLinecap="round"
          transform="rotate(-90 50 50)"
          style={{ transition: "stroke-dashoffset 0.8s ease" }}
        />
        <text x="50" y="46" textAnchor="middle" className="gauge-ring-pct">
          {Math.round(percent)}%
        </text>
        <text x="50" y="62" textAnchor="middle" className="gauge-ring-sublabel">
          {label}
        </text>
      </svg>
      {detail && <span className="gauge-ring-detail">{detail}</span>}
    </div>
  );
}

function StatusDot({ ok }: { ok: boolean }) {
  return (
    <span className={`health-status-dot ${ok ? "dot-ok" : "dot-err"}`} />
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="health-info-row">
      <span className="health-info-label">{label}</span>
      <span className={`health-info-value${mono ? " meta-mono" : ""}`}>{value}</span>
    </div>
  );
}

export function SystemStatus() {
  const { stats, loading: statsLoading } = useStats(30000);
  const { health, loading: healthLoading } = useHealth(30000);
  const { sources } = useSources();

  const timeAgo = (dateStr: string | null) => {
    if (!dateStr) return "Nie";
    const d = new Date(dateStr);
    const diffMin = Math.floor((Date.now() - d.getTime()) / 60000);
    if (diffMin < 1) return "Gerade eben";
    if (diffMin < 60) return `Vor ${diffMin} Min.`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `Vor ${diffH} Std.`;
    return `Vor ${Math.floor(diffH / 24)} Tagen`;
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  const rssCount = sources.filter((s) => s.type === "rss_feed").length;
  const emailCount = sources.filter((s) => s.type === "email_newsletter").length;
  const activeSourceCount = sources.filter((s) => s.enabled).length;

  const dbInfo = stats?.database_info;
  const sys: SystemMetrics | null = health?.system ?? null;

  const getGaugeColor = (pct: number) => {
    if (pct < 60) return "var(--success)";
    if (pct < 85) return "var(--warning)";
    return "var(--danger)";
  };

  return (
    <div>
      <h2 className="admin-page-title">Statistik & System-Status</h2>
      <p className="text-muted admin-page-desc">
        Übersicht über Systemgesundheit, Datenbank-Nutzung und Verarbeitungsstatistiken.
      </p>

      {/* ─── System-Gesundheit ─── */}
      <section className="card settings-section">
        <h3>System-Gesundheit</h3>
        {healthLoading ? (
          <p className="loading-text">Lade...</p>
        ) : health ? (
          <div className="health-grid">
            {/* Left: Status & Info */}
            <div className="health-info-panel">
              <div className="health-status-row">
                <StatusDot ok={health.status === "ok"} />
                <span className="health-status-text">
                  {health.status === "ok" ? "Alle Systeme betriebsbereit" : "System eingeschränkt"}
                </span>
              </div>

              <div className="health-info-list">
                <InfoRow label="API-Status" value={health.status === "ok" ? "Online" : health.status} />
                <InfoRow label="Datenbank" value={health.database === "ok" ? "Verbunden" : health.database} />
                <InfoRow label="Version" value={`v${health.version}`} />
                {sys && (
                  <>
                    <InfoRow label="Hostname" value={sys.hostname} mono />
                    <InfoRow label="Plattform" value={sys.platform} />
                    <InfoRow label="CPU-Kerne" value={String(sys.cpu_count)} />
                    <InfoRow label="Uptime" value={sys.uptime} />
                  </>
                )}
              </div>
            </div>

            {/* Right: Gauges */}
            {sys && (
              <div className="health-gauges">
                <GaugeRing
                  percent={sys.cpu_percent}
                  color={getGaugeColor(sys.cpu_percent)}
                  label="CPU"
                  detail={`${sys.cpu_count} Kerne`}
                />
                <GaugeRing
                  percent={sys.memory_percent}
                  color={getGaugeColor(sys.memory_percent)}
                  label="RAM"
                  detail={`${sys.memory_used_pretty} / ${sys.memory_total_pretty}`}
                />
                <GaugeRing
                  percent={sys.disk_percent}
                  color={getGaugeColor(sys.disk_percent)}
                  label="Disk"
                  detail={`${sys.disk_used_pretty} / ${sys.disk_total_pretty}`}
                />
              </div>
            )}
          </div>
        ) : (
          <p className="error-text">System nicht erreichbar</p>
        )}
      </section>

      {/* ─── Datenbank-Info ─── */}
      {dbInfo && (
        <section className="card settings-section">
          <h3>Datenbank</h3>
          <div className="db-info-grid">
            <div className="db-info-card db-info-highlight">
              <span className="db-info-icon">🗄️</span>
              <div>
                <span className="db-info-card-value">{dbInfo.db_size_pretty}</span>
                <span className="db-info-card-label">Datenbank-Größe</span>
              </div>
            </div>
            <div className="db-info-card">
              <span className="db-info-icon">📎</span>
              <div>
                <span className="db-info-card-value">{dbInfo.attachment_storage_pretty}</span>
                <span className="db-info-card-label">Datei-Speicher</span>
              </div>
            </div>
            <div className="db-info-card">
              <span className="db-info-icon">🤖</span>
              <div>
                <span className="db-info-card-value">
                  {dbInfo.ai_processed_count}
                  <small> ({dbInfo.ai_processed_pct}%)</small>
                </span>
                <span className="db-info-card-label">KI-verarbeitet</span>
              </div>
            </div>
            <div className="db-info-card">
              <span className="db-info-icon">🖼️</span>
              <div>
                <span className="db-info-card-value">{dbInfo.ai_images_count}</span>
                <span className="db-info-card-label">KI-Bilder</span>
              </div>
            </div>
          </div>

          <div className="health-info-list" style={{ marginTop: 16 }}>
            <InfoRow label="Datenbank-Name" value={dbInfo.db_name} mono />
            <InfoRow label="Ältester Artikel" value={formatDate(dbInfo.oldest_item_at)} />
            <InfoRow label="Neuester Artikel" value={formatDate(dbInfo.newest_item_at)} />
          </div>
        </section>
      )}

      {/* ─── Datenbank-Statistiken ─── */}
      {stats && (
        <>
          <section className="card settings-section">
            <h3>Datenbank-Übersicht</h3>
            <div className="db-stat-grid">
              <div className="db-stat-card">
                <span className="stat-value" style={{ color: "var(--primary)" }}>{stats.total_items}</span>
                <span className="stat-label">Gesamt-Artikel</span>
              </div>
              <div className="db-stat-card">
                <span className="stat-value" style={{ color: "var(--warning)" }}>{stats.unread_items}</span>
                <span className="stat-label">Ungelesen</span>
              </div>
              <div className="db-stat-card">
                <span className="stat-value" style={{ color: "var(--success)" }}>{stats.items_today}</span>
                <span className="stat-label">Heute importiert</span>
              </div>
              <div className="db-stat-card">
                <span className="stat-value" style={{ color: "var(--danger)" }}>{stats.errors_today}</span>
                <span className="stat-label">Fehler heute</span>
              </div>
              <div className="db-stat-card">
                <span className="stat-value">{stats.total_attachments}</span>
                <span className="stat-label">Anhänge</span>
              </div>
              <div className="db-stat-card">
                <span className="stat-value">{stats.total_links}</span>
                <span className="stat-label">Links</span>
              </div>
            </div>
          </section>

          <section className="card settings-section">
            <h3>Quellen</h3>
            <div className="db-stat-grid">
              <div className="db-stat-card">
                <span className="stat-value">{sources.length}</span>
                <span className="stat-label">Gesamt Quellen</span>
              </div>
              <div className="db-stat-card">
                <span className="stat-value">{activeSourceCount}</span>
                <span className="stat-label">Aktive Quellen</span>
              </div>
              <div className="db-stat-card">
                <span className="stat-value">{rssCount}</span>
                <span className="stat-label">RSS-Feeds</span>
              </div>
              <div className="db-stat-card">
                <span className="stat-value">{emailCount}</span>
                <span className="stat-label">E-Mail Newsletter</span>
              </div>
            </div>
          </section>

          <section className="card settings-section">
            <h3>Letzte Aktivität</h3>
            <div className="health-info-list">
              <InfoRow label="Letzter Import" value={timeAgo(stats.last_ingest_at)} />
              <InfoRow
                label="Durchschn. Relevanz"
                value={stats.avg_relevance_score !== null ? stats.avg_relevance_score.toFixed(1) : "—"}
              />
            </div>
          </section>

          <section className="card settings-section">
            <h3>Letzte 7 Tage</h3>
            <div className="chart-bars">
              {stats.items_last_7_days.map((d) => {
                const maxCount = Math.max(...stats.items_last_7_days.map((x) => x.count), 1);
                const pct = (d.count / maxCount) * 100;
                return (
                  <div key={d.date} className="chart-bar-col">
                    <div className="chart-bar-track">
                      <div className="chart-bar-fill" style={{ height: `${pct}%` }} />
                    </div>
                    <span className="chart-bar-label">{d.date.slice(5)}</span>
                    <span className="chart-bar-value">{d.count}</span>
                  </div>
                );
              })}
            </div>
          </section>

          <section className="card settings-section">
            <h3>Status-Verteilung</h3>
            <div className="status-list">
              {Object.entries(stats.items_by_status).map(([status, count]) => (
                <div key={status} className="status-list-item">
                  <span className={`status-badge status-${status}`}>{status}</span>
                  <span>{count}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="card settings-section">
            <h3>Tag-Verteilung</h3>
            {stats.tag_distribution.length > 0 ? (
              <div className="status-list">
                {stats.tag_distribution.map((t) => (
                  <div key={t.tag} className="status-list-item">
                    <span>{t.tag}</span>
                    <span style={{ fontWeight: 600 }}>{t.count}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-muted">Noch keine Tags vorhanden.</p>
            )}
          </section>

          {/* Tabellen-Größen */}
          {dbInfo && (
            <section className="card settings-section">
              <h3>Tabellen-Größen</h3>
              <div className="status-list">
                {dbInfo.tables.map((t) => (
                  <div key={t.table_name} className="status-list-item" style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 16 }}>
                    <span className="meta-mono" style={{ fontSize: 13 }}>{t.table_name}</span>
                    <span style={{ color: "var(--text-dim)", fontSize: 13 }}>
                      {t.row_count.toLocaleString("de-DE")} Zeilen
                    </span>
                    <span style={{ fontWeight: 600, fontSize: 13, minWidth: 70, textAlign: "right" }}>
                      {t.size_pretty}
                    </span>
                  </div>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {statsLoading && !stats && <p className="loading-text">Lade Statistiken...</p>}
    </div>
  );
}
